import {
  listAdminAccessRequests,
  type AdminAccessRequest,
} from "@/lib/api/admin-access-requests";

export type ImportedIntegratorEnvironment = "sandbox" | "live";

export type ImportedIntegratorSyncStatus = "linked" | "failed" | "pending";

export type ImportedIntegratorOrigin = "imported_from_glofox";

export type ImportedIntegratorFilter =
  | "all"
  | "sandbox"
  | "live"
  | "sync_failed";

export type ImportedIntegratorListCriteria = {
  query?: string;
};

export type AdminImportedIntegrator = {
  id: string;
  integrator_id: string;
  key_environment: ImportedIntegratorEnvironment;
  origin: ImportedIntegratorOrigin;
  sync_status: ImportedIntegratorSyncStatus;
  last_sync_at: string | null;
  contact_name: string;
  email: string;
  company_name: string;
};

export type AdminImportedIntegratorSummary = {
  total: number;
  sandbox: number;
  live: number;
  syncFailed: number;
};

function matchesImportedIntegratorQuery(
  row: AdminImportedIntegrator,
  query: string,
): boolean {
  if (!query) {
    return true;
  }
  const haystack = [
    row.contact_name,
    row.email,
    row.company_name,
    row.integrator_id,
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
}

export function filterImportedIntegrators(
  rows: AdminImportedIntegrator[],
  filter: ImportedIntegratorFilter,
  criteria: ImportedIntegratorListCriteria = {},
): AdminImportedIntegrator[] {
  const query = criteria.query?.trim().toLowerCase() ?? "";

  return rows.filter((row) => {
    if (
      (filter === "sandbox" || filter === "live") &&
      row.key_environment !== filter
    ) {
      return false;
    }
    if (filter === "sync_failed" && row.sync_status !== "failed") {
      return false;
    }
    return matchesImportedIntegratorQuery(row, query);
  });
}

/** Single-page range label. This list is filtered in the browser. */
export function importedIntegratorsResultRangeLabel(visible: number): string {
  if (visible === 0) {
    return "Showing 0 integrators";
  }
  if (visible === 1) {
    return "Showing 1 of 1";
  }
  return `Showing 1–${visible} of ${visible}`;
}

export function summarizeImportedIntegrators(
  rows: AdminImportedIntegrator[],
): AdminImportedIntegratorSummary {
  return {
    total: rows.length,
    sandbox: rows.filter((row) => row.key_environment === "sandbox").length,
    live: rows.filter((row) => row.key_environment === "live").length,
    syncFailed: rows.filter((row) => row.sync_status === "failed").length,
  };
}

export function importedIntegratorEnvironmentLabel(
  environment: ImportedIntegratorEnvironment,
): string {
  return environment === "sandbox" ? "Sandbox" : "Live";
}

export function importedIntegratorSyncLabel(
  status: ImportedIntegratorSyncStatus,
): string {
  if (status === "linked") return "Synced";
  if (status === "failed") return "Sync failed";
  return "Sync pending";
}

export function importedIntegratorOriginLabel(): string {
  return "Imported from Glofox";
}

export function formatImportedIntegratorSyncDate(iso: string | null): string {
  if (!iso) {
    return "Not synced yet";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const ADMIN_LIST_PAGE_SIZE = 100;

export function importedIntegratorFromAccessRequest(
  request: AdminAccessRequest,
): AdminImportedIntegrator | null {
  if (request.origin !== "imported_from_glofox") {
    return null;
  }
  if (request.environment !== "sandbox" && request.environment !== "live") {
    return null;
  }
  const syncStatus = request.sync_status;
  if (syncStatus !== "linked" && syncStatus !== "failed" && syncStatus !== "pending") {
    return null;
  }
  return {
    id: request.id,
    integrator_id: request.integrator_id?.trim() || "Not linked yet",
    key_environment: request.environment,
    origin: "imported_from_glofox",
    sync_status: syncStatus,
    last_sync_at: request.last_synced_at ?? null,
    contact_name: request.contact_name,
    email: request.email,
    company_name: request.company_name,
  };
}

export async function listAdminImportedIntegrators(): Promise<{
  integrators: AdminImportedIntegrator[];
}> {
  const imported: AdminImportedIntegrator[] = [];
  let page = 1;

  for (;;) {
    const response = await listAdminAccessRequests({
      page,
      limit: ADMIN_LIST_PAGE_SIZE,
    });
    for (const request of response.requests) {
      const row = importedIntegratorFromAccessRequest(request);
      if (row) {
        imported.push(row);
      }
    }
    if (
      response.requests.length === 0 ||
      page * ADMIN_LIST_PAGE_SIZE >= response.total
    ) {
      return { integrators: imported };
    }
    page += 1;
  }
}
